import { ChevronDown, RefreshCw } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { liveVehicleApi } from './liveVehicleApi';
import {
  mapTelematicsDetailToVehicle,
  mapTelematicsMetricsToVehicle,
} from './liveVehicleMappers';
import VehicleInfoPanel from './VehicleInfoPanel';

const DEFAULT_VEHICLE_IMAGE =
  'https://images.unsplash.com/photo-1568605117036-5fe5e7dbe0e7?w=120&h=120&fit=crop&q=80';

const VEHICLE_DETAIL_POLL_MS = 5000;

function patrolToVehicle(patrol) {
  const id = patrol.patrol_id ?? patrol.id ?? String(patrol);
  const time = patrol.last_connected_at ?? patrol.lastConnectedAt ?? '';
  const lastConnectedAt = time || '—';

  return {
    id,
    locationLabel: patrol.name ?? patrol.license_plate ?? '',
    licensePlate:
      patrol.license_plate ??
      patrol.licensePlate ??
      patrol.label ??
      patrol.name ??
      `Vehicle ${id}`,
    lastConnectedAt,
    imageUrl: patrol.image_url ?? patrol.imageUrl ?? DEFAULT_VEHICLE_IMAGE,
  };
}

/**
 * Right-hand panel for live vehicle tracking: header, search, and vehicle cards.
 */
const VehicleDetails = ({
  title = 'Live Vehicles',
  searchPlaceholder = 'Search by Vehicle license Plate',
  searchQuery = '',
  onSearchChange,
  onRefresh,
  isRefreshing = false,
  isListLoading = false,
  listError = null,
  patrols = [],
  onPatrolSelect,
  onPatrolPositionUpdate,
  selectedPatrolId = null,
  selectedVehicle = null,
  onBack,
}) => {
  const hasPatrols = Array.isArray(patrols) && patrols.length > 0;

  const [detailVehicle, setDetailVehicle] = useState(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [metricsError, setMetricsError] = useState(null);

  const loadVehicleDetail = useCallback(async (phone, listItem) => {
    const [deviceResult, metricsResult] = await Promise.allSettled([
      liveVehicleApi.getTelematicsDevice(phone, { decode: true }),
      liveVehicleApi.getDeviceMetrics(phone, { hours: 24 }),
    ]);

    if (deviceResult.status !== 'fulfilled') {
      throw deviceResult.reason;
    }

    let vehicle = mapTelematicsDetailToVehicle(deviceResult.value, listItem);

    if (metricsResult.status === 'fulfilled') {
      vehicle = { ...vehicle, ...mapTelematicsMetricsToVehicle(metricsResult.value) };
      setMetricsError(null);
    } else {
      console.error('Failed to load vehicle metrics:', metricsResult.reason);
      setMetricsError(
        metricsResult.reason?.message ?? 'Failed to load fleet KPIs.'
      );
    }

    return vehicle;
  }, []);

  const terminalPhone =
    selectedVehicle?.terminal_phone ??
    selectedVehicle?.patrol_id ??
    selectedVehicle?.id ??
    null;

  useEffect(() => {
    if (!terminalPhone) {
      setDetailVehicle(null);
      setDetailError(null);
      setIsDetailLoading(false);
      return;
    }

    let cancelled = false;

    const fetchDetail = async (isInitial = false) => {
      if (isInitial) {
        setIsDetailLoading(true);
        setDetailError(null);
        setMetricsError(null);
      }
      try {
        const vehicle = await loadVehicleDetail(terminalPhone, selectedVehicle);
        if (cancelled) return;
        setDetailVehicle(vehicle);
        if (vehicle?.has_position && vehicle.lat != null && vehicle.lng != null) {
          onPatrolPositionUpdate?.(terminalPhone, {
            lat: vehicle.lat,
            lng: vehicle.lng,
            has_position: true,
            speed_kmh: vehicle.speed_kmh ?? null,
            heading: vehicle.direction_degrees ?? null,
          });
        }
        if (isInitial) setDetailError(null);
      } catch (error) {
        if (cancelled) return;
        if (isInitial) {
          console.error('Failed to load vehicle detail:', error);
          setDetailError(error?.message ?? 'Failed to load vehicle details.');
          setDetailVehicle(null);
          setMetricsError(null);
        } else {
          console.error('Failed to refresh vehicle detail:', error);
        }
      } finally {
        if (!cancelled && isInitial) setIsDetailLoading(false);
      }
    };

    fetchDetail(true);

    const pollId = setInterval(() => {
      fetchDetail(false);
    }, VEHICLE_DETAIL_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(pollId);
    };
  }, [terminalPhone, selectedVehicle, loadVehicleDetail, onPatrolPositionUpdate]);

  const vehicles = useMemo(() => {
    const base = hasPatrols ? patrols.map(patrolToVehicle) : [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return base;
    return base.filter(v => {
      const plate = String(v.licensePlate ?? '').toLowerCase();
      const connected = String(v.lastConnectedAt ?? '').toLowerCase();
      const loc = String(v.locationLabel ?? '').toLowerCase();
      return plate.includes(q) || connected.includes(q) || loc.includes(q);
    });
  }, [hasPatrols, patrols, searchQuery]);

  const handleRetryDetail = () => {
    if (!terminalPhone) return;
    setDetailVehicle(null);
    setDetailError(null);
    setMetricsError(null);
    setIsDetailLoading(true);
    loadVehicleDetail(terminalPhone, selectedVehicle)
      .then(vehicle => {
        setDetailVehicle(vehicle);
        if (vehicle?.has_position && vehicle.lat != null && vehicle.lng != null) {
          onPatrolPositionUpdate?.(terminalPhone, {
            lat: vehicle.lat,
            lng: vehicle.lng,
            has_position: true,
            speed_kmh: vehicle.speed_kmh ?? null,
            heading: vehicle.direction_degrees ?? null,
          });
        }
        setDetailError(null);
      })
      .catch(error => {
        setDetailError(error?.message ?? 'Failed to load vehicle details.');
      })
      .finally(() => setIsDetailLoading(false));
  };

  if (selectedVehicle) {
    return (
      <VehicleInfoPanel
        vehicle={detailVehicle}
        listVehicle={selectedVehicle}
        isLoading={isDetailLoading}
        loadError={detailError}
        metricsError={metricsError}
        onRetry={handleRetryDetail}
        onBack={onBack}
      />
    );
  }

  return (
    <aside
      className="flex h-full min-h-0 w-full shrink-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white/70 backdrop-blur-md shadow-sm md:max-h-[100vh]"
      aria-label="Live patrol details"
    >
      <header className="flex shrink-0 items-center justify-between gap-3 px-5 pb-3 pt-5">
        <h1 className="text-lg font-bold leading-tight text-slate-900">{title}</h1>
        <button
          type="button"
          onClick={onRefresh}
          disabled={isRefreshing}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sky-600 transition-colors hover:bg-sky-50 disabled:opacity-50"
          aria-label="Refresh patrol list"
        >
          <RefreshCw
            className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`}
            strokeWidth={2}
          />
        </button>
      </header>

      <div className="shrink-0 px-5 pb-4">
        <div className="relative flex items-center">
          <span className="pointer-events-none absolute left-3 flex text-slate-400" aria-hidden>
            <ChevronDown className="h-4 w-4" strokeWidth={2} />
          </span>
          <input
            type="search"
            value={searchQuery}
            onChange={e => onSearchChange?.(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full rounded-[10px] border border-slate-200/90 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-800 shadow-inner outline-none ring-sky-400/30 placeholder:text-slate-400 focus:border-sky-300 focus:ring-2"
            aria-label={searchPlaceholder}
          />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-3 pb-4">
        {listError ? (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {listError}
          </div>
        ) : null}

        {isListLoading && !hasPatrols ? (
          <div className="flex min-h-0 flex-1 items-center justify-center rounded-xl bg-slate-50/90 px-4 py-12">
            <p className="text-center text-[15px] text-slate-500">Loading vehicles…</p>
          </div>
        ) : vehicles.length === 0 ? (
          <div className="flex min-h-0 flex-1 items-center justify-center rounded-xl bg-slate-50/90 px-4 py-12">
            <p className="text-center text-[15px] text-slate-500">
              {listError
                ? 'Could not load vehicles.'
                : hasPatrols
                  ? 'No vehicles match your search.'
                  : 'No vehicles to show.'}
            </p>
          </div>
        ) : (
          <ul className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-y-contain px-2 pb-2 [-webkit-overflow-scrolling:touch]">
            {vehicles.map(v => {
              const selected = selectedPatrolId != null && v.id === selectedPatrolId;
              const fullPatrol = hasPatrols
                ? patrols.find(p => (p.patrol_id ?? p.id) === v.id)
                : null;
              const noGps = fullPatrol && !fullPatrol.has_position;

              return (
                <li key={v.id}>
                  <button
                    type="button"
                    onClick={() => onPatrolSelect?.(fullPatrol)}
                    className={`flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-colors ${
                      selected
                        ? 'border-sky-400 bg-white shadow-md ring-1 ring-sky-100'
                        : 'border-slate-100 bg-white shadow-sm hover:border-slate-200 hover:shadow'
                    }`}
                  >
                    <img
                      src={v.imageUrl}
                      alt=""
                      className="h-[60px] w-[60px] shrink-0 rounded-lg object-cover"
                      loading="lazy"
                    />
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="truncate text-base font-semibold text-slate-900">
                        {v.licensePlate}
                      </span>
                      {v.lastConnectedAt ? (
                        <span className="truncate text-sm text-slate-600">
                          {v.lastConnectedAt}
                        </span>
                      ) : null}
                      {noGps ? (
                        <span className="truncate text-xs text-amber-600">
                          No GPS fix — not on map
                        </span>
                      ) : null}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
};

export default VehicleDetails;
